import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  EnvelopeOpenIcon,
  MapPinIcon,
  PencilIcon,
  PhoneIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import { Button, Card, CardBody, Chip, Divider, Link } from '@heroui/react';
import type { FC } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router-dom';
import AccountConnectedReps from '@/app/components/account/account-connected-reps.tsx';
import AccountDeactivatedBadge from '@/app/components/account/account-deactivated-badge.tsx';
import AccountFlagBadge from '@/app/components/account/account-flag-badge.tsx';
import NoAccountBanner from '@/app/components/account/no-account-banner.tsx';
import { PageLoading } from '@/app/components/page-loading.tsx';
import { useAuth } from '@/app/hooks/auth-provider';
import { useRouteAccount } from '@/app/hooks/use-route-account.hook.ts';
import { hasPermission } from '../../../types/constants.ts';
import { type StaffMemberModel, StaffRoleEnum } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';

type AccountInfoProps = {};

const AccountInfo: FC<AccountInfoProps> = () => {
  const navigate = useNavigate();
  const { user, isImpersonating } = useAuth();
  const { account, isLoading } = useRouteAccount({ basePath: '/account/info' });
  const isAdminViewer = user?.userType === UserTypeEnum.Admin && !isImpersonating;

  const canEdit =
    user?.userType === UserTypeEnum.Admin ||
    (user?.userType === UserTypeEnum.StaffMember &&
      hasPermission(user as StaffMemberModel, StaffRoleEnum.SiteAdministrator, account?.id));

  if (isLoading) {
    return <PageLoading />;
  }

  if (!account) {
    return (
      <div className='flex flex-col gap-4'>
        <h2 className='text-xl'>Account Info</h2>
        <NoAccountBanner />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex items-center justify-between'>
        <Button
          size='sm'
          variant='light'
          color='default'
          onPress={() => navigate(-1)}
          startContent={<ArrowLeftIcon className='size-4' />}
        >
          Back
        </Button>
        {canEdit && (
          <div className='flex items-center gap-2'>
            <Button
              size='sm'
              variant='flat'
              color='default'
              as={Link}
              href={`/account/edit/${account?.key}`}
              startContent={<PencilIcon className='size-4' />}
            >
              Edit Details
            </Button>
            <Button
              size='sm'
              variant='flat'
              color='primary'
              as={Link}
              href={`/account/settings/${account?.key}`}
              startContent={<Cog6ToothIcon className='size-4' />}
            >
              Settings
            </Button>
          </div>
        )}
      </div>

      <Card shadow='sm' className='max-w-screen-lg self-center w-full mt-4'>
        <CardBody className='p-6'>
          <div className='flex items-start gap-4 mb-6'>
            <div className='p-3 rounded-lg bg-primary-100'>
              <BuildingOffice2Icon className='size-8 text-primary-600' />
            </div>
            <div className='flex-1'>
              <div className='flex flex-wrap items-center gap-2'>
                <h1 className='text-2xl font-bold text-gray-900'>{account?.accountName}</h1>
                <AccountFlagBadge accountFlagId={account.accountFlagId} />
                <AccountDeactivatedBadge deactivatedAt={account.deactivatedAt} />
              </div>
              <p className='text-sm text-gray-500 mt-1'>
                {account?.other ?? account?.accountType?.description}
              </p>
              {account?.organization && (
                <p className='text-sm text-gray-600 mt-1'>{account.organization.name}</p>
              )}
            </div>
          </div>

          <Divider className='my-6' />

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900 mb-4'>Contact Information</h2>
              <div className='flex flex-col gap-4'>
                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-lg bg-gray-100'>
                    <EnvelopeOpenIcon className='size-5 text-gray-600' />
                  </div>
                  <div className='flex-1'>
                    <p className='text-xs font-medium text-gray-500 mb-1'>Email</p>
                    <a
                      href={`mailto:${account?.emailAddress}`}
                      className='text-sm text-primary-600 hover:text-primary-700'
                    >
                      {account?.emailAddress}
                    </a>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-lg bg-gray-100'>
                    <PhoneIcon className='size-5 text-gray-600' />
                  </div>
                  <div className='flex-1'>
                    <p className='text-xs font-medium text-gray-500 mb-1'>Phone</p>
                    <a
                      href={`tel:${account?.phoneNumber}`}
                      className='text-sm text-primary-600 hover:text-primary-700'
                    >
                      {formatPhoneNumber(account?.phoneNumber)}
                    </a>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-lg bg-gray-100'>
                    <MapPinIcon className='size-5 text-gray-600' />
                  </div>
                  <div className='flex-1'>
                    <p className='text-xs font-medium text-gray-500 mb-1'>Address</p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${account?.address?.line1} ${account?.address?.city} ${account?.address?.state.abbreviation} ${account?.address?.zipCode}`)}`}
                      target='_blank'
                      rel='noreferrer'
                      className='text-sm text-primary-600 hover:text-primary-700 flex flex-col'
                    >
                      <span>{account?.address?.line1}</span>
                      {account?.address?.line2 && <span>{account?.address?.line2}</span>}
                      <span>
                        {account?.address?.city}, {account?.address?.state?.abbreviation}{' '}
                        {account?.address.zipCode}
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className='text-lg font-semibold text-gray-900 mb-4'>Office Meeting Info</h2>
              <div className='flex flex-col gap-4'>
                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-lg bg-gray-100'>
                    <UserGroupIcon className='size-5 text-gray-600' />
                  </div>
                  <div className='flex-1'>
                    <p className='text-xs font-medium text-gray-500 mb-1'>Headcount</p>
                    <p className='text-sm text-gray-700'>{account?.mealHeadcount ?? '--'}</p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-lg bg-gray-100'>
                    <DocumentTextIcon className='size-5 text-gray-600' />
                  </div>
                  <div className='flex-1'>
                    <p className='text-xs font-medium text-gray-500 mb-1'>Meal Instructions</p>
                    <p className='text-sm text-gray-700'>{account?.mealInstructions || '--'}</p>
                  </div>
                </div>
              </div>

              {account?.specialties?.length > 0 && (
                <>
                  <h2 className='text-lg font-semibold text-gray-900 mb-4'>Specialties</h2>
                  <div className='flex flex-wrap gap-2'>
                    {account.specialties.map(value => (
                      <Chip key={value.id} size='md' variant='flat' color='primary'>
                        {value.description}
                      </Chip>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {isAdminViewer && (
        <AccountConnectedReps
          accountKey={account.key}
          accountId={account.id}
          accountName={account.accountName}
        />
      )}
    </div>
  );
};

export default AccountInfo;
